import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SeoFooter } from "@/components/SeoFooter";
import { MobileMenu } from "@/components/MobileMenu";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";
import {
  Megaphone, Gavel, UserCheck, ShieldCheck, Eye, RefreshCw, BadgeDollarSign,
  ArrowRight, Sparkles, Instagram, Youtube, Facebook, Star, TrendingUp, Lock,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const STEPS = [
  { icon: Megaphone, title: "Post your requirement", desc: "Define objective, deliverables, platforms, audience, timeline & budget." },
  { icon: Gavel, title: "Receive bids", desc: "Relevant creators discover your campaign and submit tailored offers." },
  { icon: UserCheck, title: "Select your creator", desc: "Compare profiles, portfolios, reach & engagement. Pick the best fit." },
  { icon: ShieldCheck, title: "Secure the payment", desc: "Funds are held safely on the platform until you approve the work." },
  { icon: Eye, title: "Protected preview", desc: "Review watermarked content before it's ever released." },
  { icon: BadgeDollarSign, title: "Approve & release", desc: "Happy? Release payment. One transaction, one secure workflow." },
];

export default function Landing() {
  const { openAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("join")) { openAuth(); setParams({}, { replace: true }); }
  }, [params, openAuth, setParams]);
  return (
    <div className="relative overflow-x-clip">
      <div className="grain" />
      {/* glowing orbs */}
      <div className="glow-orb bg-neon-pink" style={{ width: 420, height: 420, top: -80, right: -60 }} />
      <div className="glow-orb bg-neon-purple" style={{ width: 380, height: 380, top: 400, left: -120 }} />
      <div className="glow-orb bg-brand-pink" style={{ width: 300, height: 300, bottom: 200, right: 100 }} />
      <div className="hero-glow absolute top-0 inset-x-0 h-[820px] z-0 pointer-events-none" />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/80 border-b border-black/5" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground font-medium">
            <a href="#how" data-testid="nav-how-link" className="hover:text-foreground transition-colors">How it works</a>
            <a href="#features" data-testid="nav-features-link" className="hover:text-foreground transition-colors">Features</a>
            <button type="button" onClick={openAuth} data-testid="nav-post-campaign-link" className="hover:text-foreground transition-colors">Post a Campaign</button>
            <Link to="/influencer-directory" data-testid="nav-find-influencers-link" className="hover:text-foreground transition-colors">Find Influencers</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button onClick={openAuth} data-testid="nav-login-btn" className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold hover:-translate-y-0.5 transition-transform">
              Sign in / Sign up
            </Button>
            <MobileMenu openAuth={openAuth} />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon-lime/30 bg-neon-lime/10 text-neon-lime text-xs font-bold uppercase tracking-[0.2em] mb-7">
              <Sparkles className="h-3.5 w-3.5" /> Brands meet Creators
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp} className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Turn your campaign idea into <span className="text-gradient">influencer content</span>.
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp} className="mt-7 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Post your requirement. Set your budget. Receive bids from creators. Approve the influencer. Release payment only after you're satisfied.
            </motion.p>
            <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-9 flex flex-wrap gap-4">
              <Button onClick={openAuth} data-testid="hero-post-campaign-btn" size="lg" className="rounded-full h-13 px-8 py-6 text-base bg-brand-gradient text-white hover:opacity-95 font-bold hover:-translate-y-0.5 transition-all shadow-[0_12px_30px_-8px_rgba(255,46,147,0.6)]">
                Post a Campaign <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button onClick={openAuth} data-testid="hero-find-influencers-btn" size="lg" variant="outline" className="rounded-full h-13 px-8 py-6 text-base border border-brand-pink/30 bg-gradient-to-r from-brand-orange/10 via-brand-pink/10 to-brand-blue/10 hover:from-brand-orange/20 hover:via-brand-pink/20 hover:to-brand-blue/20 text-foreground font-semibold hover:-translate-y-0.5 transition-all">
                <span className="text-gradient font-bold">Find Influencers</span>
              </Button>
            </motion.div>
            <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-neon-cyan" /> Secure payments</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-neon-cyan" /> Protected content</div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-black/5">
              <img src="https://images.unsplash.com/photo-1664277497087-51787d2b9d15?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Creator at work" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-black/5 bg-background/70 backdrop-blur-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Live bid</p>
                  <p className="font-heading font-bold text-lg">Summer Reel Campaign</p>
                </div>
                <div className="text-right">
                  <p className="text-neon-lime font-heading font-black text-2xl">₹24,000</p>
                  <p className="text-xs text-muted-foreground">7 bids</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -left-4 rounded-2xl border border-black/5 bg-surface p-3 flex items-center gap-2 shadow-xl">
              <Instagram className="h-5 w-5 text-neon-pink" />
              <Youtube className="h-5 w-5 text-destructive" />
              <Facebook className="h-5 w-5 text-neon-cyan" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Logos marquee */}
      <section className="relative z-10 border-y border-black/5 py-6 overflow-hidden">
        <div className="flex items-center gap-16 whitespace-nowrap animate-marquee text-muted-foreground/60 font-heading font-bold text-xl">
          {["Fashion", "Beauty", "Tech", "Food", "Travel", "Fitness", "Gaming", "Finance", "Fashion", "Beauty", "Tech", "Food", "Travel", "Fitness", "Gaming", "Finance"].map((t, i) => (
            <span key={i} className="flex items-center gap-16"><Star className="h-4 w-4 text-neon-lime" /> {t}</span>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-28">
        <div className="max-w-2xl mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-4">How it works</p>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">A trusted campaign flow, end to end.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp} className="group rounded-2xl border border-border bg-surface p-7 hover:bg-surfaceHover transition-colors">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-12 w-12 rounded-xl bg-neon-lime/10 border border-neon-lime/20 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-neon-lime" />
                  </div>
                  <span className="font-heading font-black text-4xl text-black/[0.06] group-hover:text-brand-pink/30 transition-colors">0{i + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-5 auto-rows-[220px]">
          <div className="lg:col-span-7 lg:row-span-2 rounded-3xl border border-border bg-surface p-9 relative overflow-hidden flex flex-col justify-end">
            <img src="https://images.unsplash.com/photo-1625463006115-09f08489f591?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="community" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/60 to-transparent" />
            <div className="relative">
              <ShieldCheck className="h-8 w-8 text-neon-cyan mb-4" />
              <h3 className="font-heading font-black text-3xl mb-3">Content protection built-in</h3>
              <p className="text-muted-foreground max-w-md">Influencers upload completed work and you get a watermarked preview. The final content unlocks only after you approve and release payment.</p>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-3xl border border-border bg-brand-gradient p-9 text-white flex flex-col justify-between">
            <TrendingUp className="h-8 w-8" />
            <div>
              <h3 className="font-heading font-black text-3xl mb-2">Let creators compete</h3>
              <p className="font-medium opacity-80">One campaign. Multiple bids. You decide who to hire.</p>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-3xl border border-border bg-surface p-9 flex flex-col justify-between">
            <RefreshCw className="h-8 w-8 text-neon-pink" />
            <div>
              <h3 className="font-heading font-bold text-2xl mb-2">Revisions, managed</h3>
              <p className="text-muted-foreground text-sm">Request changes or approve — every revision stays inside the campaign workspace.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-4">Every niche covered</p>
        <h2 className="font-heading font-black text-4xl mb-10">Influencer categories</h2>
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <span key={c} className="px-5 py-2.5 rounded-full border border-border bg-surface text-sm font-medium hover:border-neon-lime hover:text-neon-lime transition-colors">
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="rounded-3xl border border-neon-lime/20 bg-gradient-to-br from-neon-lime/10 to-transparent p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="glow-orb bg-neon-lime" style={{ width: 400, height: 400, top: -150, left: "50%", transform: "translateX(-50%)" }} />
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-lime mb-5">Simple, transparent pricing</p>
            <h2 className="font-heading font-black text-5xl sm:text-7xl tracking-tighter">Only <span className="text-gradient">20%</span></h2>
            <p className="text-xl font-heading font-semibold mt-3">platform processing fee on successful transactions</p>
            <p className="text-muted-foreground mt-5 max-w-lg mx-auto">No monthly plans. No chasing invoices. No negotiating outside the platform. One campaign, one transaction, one secure workflow.</p>
            <Button onClick={openAuth} data-testid="pricing-cta-btn" size="lg" className="mt-9 rounded-full h-13 px-10 py-6 text-base bg-neon-lime text-background hover:bg-neon-lime/90 font-bold hover:-translate-y-0.5 transition-transform">
              Start your campaign today
            </Button>
          </div>
        </div>
      </section>

      {/* Dual CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-5">
        <div className="rounded-3xl border border-border bg-surface p-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-4">For promoters</p>
          <h3 className="font-heading font-black text-3xl mb-4">Your campaign. Your budget. Your choice.</h3>
          <p className="text-muted-foreground mb-7">Post once and let relevant influencers come to you. Compare proposals, control your budget, protect your payment.</p>
          <Button onClick={openAuth} data-testid="cta-promoter-btn" className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold">Post a Campaign</Button>
        </div>
        <div className="rounded-3xl border border-border bg-surface p-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-4">For influencers</p>
          <h3 className="font-heading font-black text-3xl mb-4">Turn your audience into business.</h3>
          <p className="text-muted-foreground mb-7">Find paid collaborations, set your price, build your reputation and get paid through a structured workflow.</p>
          <Button onClick={openAuth} data-testid="cta-influencer-btn" className="rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-semibold">Create Influencer Profile</Button>
        </div>
      </section>

      <title>InfluencerCollab — Find Influencers & Brand Collaborations</title>
      <meta name="description" content="InfluencerCollab connects brands with verified influencers and UGC creators. Post paid or gift-hamper campaigns, compare creator bids and pay safely through escrow." />
      <SeoFooter />
    </div>
  );
}
